/**
 * NotificationModalHost.tsx
 *
 * Mounted once inside `Layout.tsx`. Watches `useNotificationModalStore` and
 * renders whichever detail modal the active notification target requires.
 *
 * Modal selection mirrors what the rest of the app uses elsewhere (the
 * "currently used" modal in each domain) so clicking a notification opens
 * the same UI staff already know:
 *   - Lead (typeID 1, 21)            → LeadDetailModal
 *   - Payment (5, 12, 20)            → RecordPatientPaymentModal
 *   - Appointment (6-10, 15)         → UnifiedAppointmentModal (fetched by ID)
 *   - Note (16)                      → NoteViewModal
 *   - Survey response / e-form (18)  → SurveyResponseModal
 *
 * The remaining notification types (messages, labs, clinical) don't have a
 * standalone detail modal in the active UX, so they fall through to a route
 * navigation in `notification-display.ts` and never reach this host.
 */
import { useQuery } from '@tanstack/react-query';
import { useNotificationModalStore } from '@/store/notificationModalStore';
import { LeadDetailModal } from '@/pages/leads/LeadDetailModal';
import { RecordPatientPaymentModal } from '@/pages/payments/RecordPatientPaymentModal';
import { NoteViewModal } from '@/v5/components/NoteViewModal';
import { SurveyResponseModal } from '@/pages/patients/SurveyResponseModal';
import { UnifiedAppointmentModal } from '@/pages/appointments/UnifiedAppointmentModal';
import { appointmentsService } from '@/services/appointments.service';

export function NotificationModalHost() {
  const active = useNotificationModalStore((s) => s.active);
  const close = useNotificationModalStore((s) => s.close);

  if (!active) return null;

  switch (active.kind) {
    case 'lead':
      return (
        <LeadDetailModal
          isOpen
          onClose={close}
          leadId={active.leadId}
        />
      );

    case 'payment':
      return (
        <RecordPatientPaymentModal
          patientId={active.patientId}
          paymentId={active.paymentId}
          open
          onClose={close}
          onSaved={close}
        />
      );

    case 'note':
      return (
        <NoteViewModal
          patientId={String(active.patientId)}
          noteId={active.noteId}
          onClose={close}
          onEdit={() => close()}
        />
      );

    case 'surveyResponse':
      return (
        <SurveyResponseModal
          patientId={active.patientId}
          responseId={active.responseId}
          open
          onClose={close}
        />
      );

    case 'appointment':
      return (
        <NotificationAppointmentModal
          appointmentId={active.appointmentId}
          onClose={close}
        />
      );
  }
}

/**
 * UnifiedAppointmentModal expects the full Appointment object as a prop, not
 * an id, so we fetch by id here before mounting it. The popup-click latency
 * is acceptable; we keep the modal closed (returning null) until the row is
 * loaded rather than mount an empty shell that triggers premature edit-mode
 * effects.
 */
function NotificationAppointmentModal({
  appointmentId,
  onClose,
}: {
  appointmentId: number;
  onClose: () => void;
}) {
  const { data: appointment } = useQuery({
    queryKey: ['notification-appointment', appointmentId],
    queryFn: () => appointmentsService.getAppointment(appointmentId),
    staleTime: 30_000,
  });

  if (!appointment) return null;

  return (
    <UnifiedAppointmentModal
      isOpen
      onClose={onClose}
      appointment={appointment}
    />
  );
}
